import React, { useState } from "react";
import ImagerApi from "../../api/ImagerApi";
import { CaptureProfile, ELightPath } from "../../Model/CaptureProfile";
import { getOneImagerCapabilities } from "../../redux/reducers/ImagerCapabilitiesReducer";
import { getModule } from "../../redux/reducers/ModuleReducer";
import { getModuleState } from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import FluorescenceOpticAndCameraControl from "./FluorescenceOpticAndCameraControls";
import ImagerFeatureControlLoader from "./ImagerFeatureControlLoader";
import UltravioletOpticAndCameraControl from "./UltravioletOpticAndCameraControl";
import VisibleOpticAndCameraControl from "./VisibleOpticAndCameraControls";
import { Fov } from "../../Model/ModuleState";
import MtiButton from "../Buttons/MtiButton";
import CommandPanelComboBox from "../CommandPanel/CommandPanelComboBox";
import LightPathUtil from "../../Utility/LightPathUtil";
import { useDispatch } from "react-redux";
import { ENotificationType } from "../../Model/ENotificationType";
import { sendNotification } from "../../redux/actions/NotificationAction";

type Props = {
  imagerIdentifier: string;
  disabled?: boolean;
  additionalControlDisabled?: boolean;
  style?: React.CSSProperties;
  disabledLightPaths?: ELightPath[];
};

export default function OpticAndCameraControl({
  imagerIdentifier,
  disabled = false,
  additionalControlDisabled = false,
  disabledLightPaths = [],
  style = {},
}: Props) {
  const dispatch = useDispatch();
  const imagerModule = useTypedSelector((state) =>
    getModule(state, imagerIdentifier)
  );
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );
  const imagerCapability = useTypedSelector((state) =>
    getOneImagerCapabilities(state, imagerModule.identifier)
  );

  const imagerState = moduleState.imager;
  const [isChangingCaptureProfile, setIsChangingCaptureProfile] =
    useState(false);

  const [isChangingLightPath, setIsChangingLightPath] = useState(false);

  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  const lightPath = imagerState.activeLightPath;

  const lightPathCapability =
    imagerCapability.lightpathCollection.byId[lightPath];

  const onCaptureProfileChanged = (cp: CaptureProfile) => {
    console.log("DEBUG", "onCaptureProfileChanged", { cp });
    setIsChangingCaptureProfile(true);
    ImagerApi.setCaptureProfile(imagerModule.identifier, cp).finally(() =>
      setIsChangingCaptureProfile(false)
    );
  };

  const onAutoExposure = () => {
    ImagerApi.autoExposure(imagerModule.identifier);
  };

  const setLightPath = (lightPath: ELightPath) => {
    if (disabledLightPaths.includes(lightPath)) {
      dispatch(
        sendNotification(
          ENotificationType.Warning,
          `${LightPathUtil.toName(
            lightPath
          )} imaging has been disabled for this plate type. This property can be changed in Plate Setup`
        )
      );
    } else {
      setIsChangingLightPath(true);
      ImagerApi.setLightpath(imagerModule.identifier, lightPath).finally(() =>
        setIsChangingLightPath(false)
      );
    }
  };

  const fovX = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager?.fovX ?? 1
  );
  const fovY = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager?.fovY ?? 1
  );
  const cropZoomX = useTypedSelector(
    (state) => state.liveImageStore.liveImageCropZoomX
  );
  const cropZoomY = useTypedSelector(
    (state) => state.liveImageStore.liveImageCropZoomY
  );

  const fov: Fov = {
    width: fovX / cropZoomX,
    height: fovY / cropZoomY,
  };

  let control: JSX.Element;
  let loadSettingControl: JSX.Element;

  switch (lightPath) {
    case ELightPath.Visible:
      if (imagerState.captureProfile.visible == null) {
        throw new Error("Visible capture profile is null");
      }
      control = (
        <VisibleOpticAndCameraControl
          fov={fov}
          disabled={disabled || isChangingCaptureProfile || isChangingLightPath}
          lightPathCapability={lightPathCapability}
          visibleCaptureProperty={imagerState.captureProfile.visible}
          onVisibleCapturePropertyChanged={(visible) =>
            onCaptureProfileChanged({
              lightpath: ELightPath.Visible,
              visible: visible,
            })
          }
          onAutoExposure={onAutoExposure}
          imagerState={imagerState}
        />
      );
      break;
    case ELightPath.Fluorescence:
      if (imagerState.captureProfile.fluorescence == null) {
        throw new Error("Fluorescence capture profile is null");
      }
      control = (
        <FluorescenceOpticAndCameraControl
          fov={fov}
          disabled={disabled || isChangingCaptureProfile || isChangingLightPath}
          lightPathCapability={lightPathCapability}
          fluorescenceCaptureProperty={imagerState.captureProfile.fluorescence}
          onFluorescenceCapturePropertyChanged={(fluorescence) =>
            onCaptureProfileChanged({
              lightpath: ELightPath.Fluorescence,
              fluorescence: fluorescence,
            })
          }
          onAutoExposure={onAutoExposure}
          imagerState={imagerState}
        />
      );
      break;
    case ELightPath.Ultraviolet:
      if (imagerState.captureProfile.ultraviolet == null) {
        throw new Error("Ultraviolet capture profile is null");
      }
      control = (
        <UltravioletOpticAndCameraControl
          fov={fov}
          disabled={disabled || isChangingCaptureProfile || isChangingLightPath}
          lightPathCapability={lightPathCapability}
          ultravioletCaptureProperty={imagerState.captureProfile.ultraviolet}
          onUltravioletCapturePropertyChanged={(ultraviolet) =>
            onCaptureProfileChanged({
              lightpath: ELightPath.Ultraviolet,
              ultraviolet: ultraviolet,
            })
          }
          imagerState={imagerState}
        />
      );
      break;
    default:
      control = <div />;
      loadSettingControl = <div />;
      break;
  }

  loadSettingControl = (
    <ImagerFeatureControlLoader
      disabled={disabled || isChangingCaptureProfile || isChangingLightPath}
      imagerIdentifier={imagerModule.identifier}
      lightPathCapability={lightPathCapability}
      lightPath={lightPath}
    />
  );

  return (
    <div>
      {/*--------LIGHTPATH CONTROL--------*/}
      <CommandPanelComboBox<number>
        disabled={setLightPath == null || isChangingLightPath}
        label="Lightpath"
        value={imagerState?.activeLightPath}
        onChange={setLightPath != null ? setLightPath : () => {}}
        displayValues={imagerCapability.lightpathCollection.allIds.map(
          (lightpath) => ({
            display: LightPathUtil.toName(lightpath),
            value: lightpath,
          })
        )}
        style={{
          fontSize: 14,
          color: "#1C1C1C",
          backgroundColor: "#FFFFFF",
          paddingLeft: 19,
          paddingRight: 11,
        }}
        labelStyle={{
          width: 90,
        }}
      />

      {/*--------OPTIC AND CAMERA CONTROL--------*/}
      <div
        style={{
          margin: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              minHeight: 100,
              border: "1px solid #E0E0E0",
              borderRadius: "10px",
              padding: "9px 11px 9px 11px",
            }}
          >
            {loadSettingControl}
            {control}
          </div>
        </div>

        {/*--------ADDITIONAL CONTROL--------*/}
        {lightPath === ELightPath.Visible && (
          <div
            style={{
              marginTop: 7,
              padding: "9px 11px 9px 11px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <MtiButton
              size="large"
              disabled={additionalControlDisabled}
              style={{
                letterSpacing: 1.25,
                paddingLeft: 16,
                paddingRight: 16,
                marginRight: 10,
              }}
              onClick={() => {
                ImagerApi.doLocateDrop(imagerModule.identifier);
              }}
            >
              Locate Drop
            </MtiButton>
            <MtiButton
              size="large"
              disabled={additionalControlDisabled}
              style={{
                letterSpacing: 1.25,
                paddingLeft: 16,
                paddingRight: 16,
              }}
              onClick={() => {
                ImagerApi.doAutoFocus(imagerModule.identifier);
              }}
            >
              Auto Focus
            </MtiButton>
          </div>
        )}
      </div>
    </div>
  );
}
